import { useState, useEffect, useCallback } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import SensorStatusGrid from '../../components/SensorStatusGrid';
import SummaryTable, { SummaryData, PerOsmCost } from '../../components/tables/SummaryTable.tsx';
import FiringAlertsTable from '../../components/tables/FiringAlertsTable.tsx';
import { nowISOString } from '../../utils/utils.ts';
import { getUser } from '../../auth/authService.ts';
import { getOsmNames, DashboardConfig } from '../../services/api.ts';

const UTILITY_LABELS: Record<string, string> = {
  electricity: 'electricity',
  gas: 'gas',
  water: 'water',
};

type OsmMover = {
  osm_name: string;
  utility: string;
  pct: number;
};

function pctChange(current: number, previous: number): number {
  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
}

function generateAnalysis(summary: SummaryData, perOsm: PerOsmCost[]): string[] {
  const paragraphs: string[] = [];

  const elecDiff = summary.electricity.current - summary.electricity.previous;
  const elecPct  = summary.electricity.previous > 0
    ? Math.abs(pctChange(summary.electricity.current, summary.electricity.previous)).toFixed(1)
    : null;

  if (elecPct !== null) {
    paragraphs.push(
      elecDiff > 0
        ? `Overall electricity costs have increased by ${elecPct}% compared to last month (£${summary.electricity.current.toFixed(2)} vs £${summary.electricity.previous.toFixed(2)}), suggesting higher consumption across monitored devices.`
        : `Overall electricity costs have decreased by ${elecPct}% compared to last month (£${summary.electricity.current.toFixed(2)} vs £${summary.electricity.previous.toFixed(2)}), indicating improved efficiency or reduced usage.`
    );
  }

  const gasDiff = summary.gas.current - summary.gas.previous;
  const gasPct  = summary.gas.previous > 0
    ? Math.abs(pctChange(summary.gas.current, summary.gas.previous)).toFixed(1)
    : null;

  if (gasPct !== null) {
    paragraphs.push(
      gasDiff > 0
        ? `Gas costs have risen by ${gasPct}% this month (£${summary.gas.current.toFixed(2)} vs £${summary.gas.previous.toFixed(2)}), which may reflect increased heating or cooking demand.`
        : `Gas costs have fallen by ${gasPct}% this month (£${summary.gas.current.toFixed(2)} vs £${summary.gas.previous.toFixed(2)}), suggesting reduced heating or cooking usage.`
    );
  } else {
    paragraphs.push('No gas meter data is available for comparison at this time.');
  }

  const waterDiff = summary.water.current - summary.water.previous;
  const waterPct  = summary.water.previous > 0
    ? Math.abs(pctChange(summary.water.current, summary.water.previous)).toFixed(1)
    : null;

  if (waterPct !== null) {
    paragraphs.push(
      waterDiff > 0
        ? `Water costs have increased by ${waterPct}% compared to last month (£${summary.water.current.toFixed(2)} vs £${summary.water.previous.toFixed(2)}). Consider reviewing water usage patterns across the site.`
        : `Water costs are down by ${waterPct}% compared to last month (£${summary.water.current.toFixed(2)} vs £${summary.water.previous.toFixed(2)}), which is a positive trend.`
    );
  } else {
    paragraphs.push('No water meter data is available for comparison at this time.');
  }

  const movers: OsmMover[] = perOsm
    .filter(o => o.previous > 0)
    .map(o => ({
      osm_name: o.osm_name,
      utility: UTILITY_LABELS[o.utility],
      pct: pctChange(o.current, o.previous),
    }));

  const increases = [...movers].filter(m => m.pct > 0).sort((a, b) => b.pct - a.pct).slice(0, 3);
  const decreases = [...movers].filter(m => m.pct < 0).sort((a, b) => a.pct - b.pct).slice(0, 3);

  if (increases.length > 0) {
    const list = increases.map(m => `${m.osm_name} (${m.utility}, +${m.pct.toFixed(1)}%)`).join(', ');
    paragraphs.push(`The locations showing the largest cost increases this month are: ${list}. These may warrant closer inspection to identify the cause of the rise.`);
  }

  if (decreases.length > 0) {
    const list = decreases.map(m => `${m.osm_name} (${m.utility}, ${m.pct.toFixed(1)}%)`).join(', ');
    paragraphs.push(`The locations with the largest cost reductions are: ${list}. This suggests improved efficiency or reduced activity in these areas.`);
  }

  if (increases.length === 0 && decreases.length === 0) {
    paragraphs.push('Insufficient historical data is available to compare individual location costs at this time.');
  }

  return paragraphs;
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{
      background: 'var(--color-bg-secondary)',
      borderRadius: 3,
      p: '1.25rem 1.5rem',
      display: 'flex',
      flexDirection: 'column',
      gap: 2,
    }}>
      <Typography sx={{
        fontFamily: "'Syne', sans-serif",
        fontSize: 13,
        fontWeight: 600,
        color: 'var(--color-text-primary)',
        m: 0,
      }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

export default function Home() {
  const [username, setUsername]       = useState<string>('');
  const [configs, setConfigs]         = useState<DashboardConfig[]>([]);
  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [perOsmData, setPerOsmData]   = useState<PerOsmCost[]>([]);

  useEffect(() => {
    getUser().then(user => {
      if (user) setUsername(user.profile.preferred_username as string ?? '');
    });
    getOsmNames().then(setConfigs).catch(console.error);
  }, []);

  const handleSummaryData = useCallback((summary: SummaryData, perOsm: PerOsmCost[]) => {
    setSummaryData(summary);
    setPerOsmData(perOsm);
  }, []);

  const analysisParagraphs = summaryData
    ? generateAnalysis(summaryData, perOsmData)
    : null;

  const now = new Date().toLocaleString('en-GB', {
    weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  });

  return (
    <Box sx={{ p: '0 1.5rem 2.5rem', maxWidth: 1280, margin: '0 auto' }}>

      {/* ── Header ── */}
      <Box sx={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        mb: 3,
        flexWrap: 'wrap',
        gap: 1,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{
            fontFamily: "'Syne', sans-serif",
            fontSize: 20,
            fontWeight: 600,
            color: 'var(--color-text-primary)',
          }}>
            Hello,{' '}
            <Box component="span" sx={{ color: '#1D9E75' }}>{username || '…'}</Box>
          </Typography>
        </Box>
        <Typography sx={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 12,
          color: 'var(--color-text-secondary)',
        }}>
          {now}
        </Typography>
      </Box>

      {/* Main grid */}
      <div className="home-grid">

        {/* Sensor status */}
        <Panel title="Sensor Status">
          <SensorStatusGrid configs={configs} />
        </Panel>

        {/* Summary table */}
        <Panel title="Monthly Cost Summary">
          <SummaryTable configs={configs} onDataLoaded={handleSummaryData} />
        </Panel>

        {/* Analysis */}
        <Panel title="Analysis">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {analysisParagraphs
              ? analysisParagraphs.map((p, i) => (
                  <Typography key={i} sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: 12,
                    color: 'var(--color-text-secondary)',
                    lineHeight: 1.7,
                  }}>
                    {p}
                  </Typography>
                ))
              : (
                <Typography sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 12,
                  color: 'var(--color-text-secondary)',
                }}>
                  Loading analysis…
                </Typography>
              )
            }
          </Box>
        </Panel>

        {/* Alerts */}
        <Panel title="Alerts">
          <FiringAlertsTable />
        </Panel>

      </div>
    </Box>
  );
}
